import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { generateLandIntel } from '@/lib/landIntel';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Sparkles, Brain, FileText, RefreshCw, Compass, ChevronRight, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Modalità compatta di "AgriNexus Land Intelligence" mostrata nella pagina annuncio.
 * - Al primo click: chiama POST /api/land-intel/{listingId} → riceve cards + compatibility + report
 * - Mostra 8 card dati + score compatibilità 0-100 con breakdown
 * - CTA verso pagina report completa /terreno/:id/report
 */
export default function LandIntelligence({ listingId }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState('idle'); // idle | loading | ready | error
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    // Prova a caricare cache all'apertura per mostrare subito il report se esiste
    if (!user) return;
    api.get(`/land-intel/${listingId}`).then(r => {
      setData(r.data);
      setState('ready');
    }).catch(() => { /* nessun report ancora: rimani idle */ });
  }, [listingId, user]);

  const run = async (force = false) => {
    if (!user) { nav('/login'); return; }
    setState('loading'); setErr('');
    try {
      const data = await generateLandIntel(listingId, force);
      setData(data);
      setState('ready');
      if (force) toast.success('Report rigenerato con dati aggiornati');
    } catch (e) {
      setErr(formatApiError(e));
      setState('error');
      toast.error(formatApiError(e));
    }
  };

  if (state === 'idle') {
    return (
      <div data-testid="landintel-hero" className="rounded-2xl border-2 border-chartreuse/40 bg-gradient-to-br from-chartreuse/5 to-transparent p-6 md:p-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 shrink-0 rounded-xl bg-foreground text-background grid place-items-center">
            <Brain size={22}/>
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-terracotta">AgriNexus Land Intelligence</div>
            <h3 className="mt-1 font-display text-xl md:text-2xl tracking-tight">Analisi intelligente di questo terreno</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-xl">
              L'AI unisce dati dell'annuncio + <b>clima reale ultimi 5 anni</b> + <b>altitudine satellitare</b> + <b>toponimo OSM verificato</b> per produrre un report agronomico onesto.
              {user ? ' Confronta anche con il tuo profilo per calcolare la compatibilità.' : ' Accedi per vedere anche la compatibilità col tuo progetto.'}
            </p>
            <Button data-testid="landintel-generate-btn" onClick={() => run(false)} className="mt-4 rounded-full h-11 px-6 bg-foreground text-background hover:opacity-90">
              <Sparkles size={15} className="mr-2"/> Analizza questo terreno
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (state === 'loading') {
    return (
      <div data-testid="landintel-loading" className="rounded-2xl border border-border bg-card p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-foreground text-background grid place-items-center animate-pulse">
            <Brain size={18}/>
          </div>
          <div>
            <div className="font-display text-lg">Analisi in corso…</div>
            <div className="text-xs text-muted-foreground">Recupero clima storico, altitudine satellitare, toponimo OSM…</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          {Array.from({length: 8}).map((_,i) => (
            <div key={i} className="h-20 rounded-xl bg-secondary/50 animate-pulse"/>
          ))}
        </div>
        <div className="mt-6 space-y-2">
          <div className="h-3 bg-secondary/50 rounded animate-pulse w-3/4"/>
          <div className="h-3 bg-secondary/50 rounded animate-pulse w-full"/>
          <div className="h-3 bg-secondary/50 rounded animate-pulse w-5/6"/>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="rounded-2xl border border-terracotta/40 bg-terracotta/5 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-terracotta shrink-0" size={20}/>
          <div className="flex-1">
            <div className="font-medium">Impossibile generare l'analisi</div>
            <div className="text-sm text-muted-foreground mt-1">{err}</div>
            <Button variant="outline" size="sm" onClick={() => run(false)} className="rounded-full mt-3">
              <RefreshCw size={12} className="mr-2"/> Riprova
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ready
  const { cards = [], compatibility = {}, geo = {}, climate, elevation_m, generated_at } = data || {};
  return (
    <div data-testid="landintel-ready" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-chartreuse text-obsidian grid place-items-center">
            <Brain size={18}/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-terracotta">AgriNexus Land Intelligence</div>
            <div className="font-display text-xl">Analisi del terreno</div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => run(true)} className="rounded-full" title="Rigenera con dati clima aggiornati">
          <RefreshCw size={12} className="mr-2"/> Rigenera
        </Button>
      </div>

      {/* Cards grid — 8 KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c, i) => (
          <div key={i} data-testid={`landintel-card-${i}`} className="rounded-xl border border-border p-4 bg-card">
            <div className="text-2xl leading-none mb-2">{c.icon}</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.label}</div>
            <div className="font-medium mt-1 text-sm leading-snug">{c.value}</div>
            {c.detail && <div className="text-[11px] text-muted-foreground mt-1.5 leading-tight line-clamp-2">{c.detail}</div>}
          </div>
        ))}
      </div>

      {/* Compatibility */}
      {compatibility.has_preferences ? (
        <CompatibilityPanel compat={compatibility}/>
      ) : (
        <div className="rounded-2xl border border-dashed border-border bg-secondary/30 p-6">
          <div className="flex items-start gap-3">
            <Compass className="text-muted-foreground shrink-0 mt-0.5" size={20}/>
            <div className="flex-1">
              <div className="font-medium">Vedi la compatibilità col tuo progetto</div>
              <div className="text-sm text-muted-foreground mt-1">
                Imposta preferenze (area, budget, zona, coltura) per calcolare un punteggio 0-100 personalizzato con motivazione.
              </div>
              <Link to="/preferenze">
                <Button size="sm" className="rounded-full mt-3">
                  Imposta il mio progetto <ChevronRight size={14} className="ml-1"/>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Sources footer */}
      <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
        {climate && <span>Clima: {climate.source}</span>}
        {elevation_m != null && <span>Elevazione: Open-Meteo</span>}
        {geo?.display_name && <span>Toponimo: OpenStreetMap</span>}
        {generated_at && <span>Generato: {new Date(generated_at).toLocaleString('it-IT')}</span>}
      </div>
      <div className="text-[11px] text-muted-foreground italic border-l-2 border-border pl-3">
        ⚠️ Report generato dall'AI: strumento di supporto informativo. Verifica sempre con un agronomo, un geometra o un notaio qualificato prima di prendere decisioni economiche o legali.
      </div>

      {/* Full report CTA */}
      <div className="rounded-2xl border-2 border-foreground bg-foreground text-background p-6 md:p-8 flex items-center justify-between flex-wrap gap-4">
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] uppercase tracking-widest font-bold text-chartreuse">Report completo</div>
          <div className="font-display text-xl md:text-2xl mt-1">Leggi il report agronomico completo</div>
          <div className="text-sm opacity-70 mt-1">Sintesi · Potenziale · Idrico · Accessibilità · Clima · Colture · Da verificare</div>
        </div>
        <Link to={`/terreno/${listingId}/report`}>
          <Button size="lg" className="rounded-full h-12 px-6 bg-chartreuse text-obsidian hover:bg-chartreuse/90" data-testid="landintel-open-report">
            <FileText size={16} className="mr-2"/> Apri report
          </Button>
        </Link>
      </div>
    </div>
  );
}

function CompatibilityPanel({ compat }) {
  const score = compat.score || 0;
  const stroke = score >= 75 ? '#B4FF00' : score >= 50 ? '#F5A623' : '#E5735F';
  const circumference = 2 * Math.PI * 42;
  const dash = (score / 100) * circumference;
  return (
    <div data-testid="landintel-compat" className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-start gap-6 flex-wrap">
        <div className="relative w-28 h-28 shrink-0">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="6" className="opacity-10"/>
            <circle cx="50" cy="50" r="42" fill="none" stroke={stroke} strokeWidth="6"
                    strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round"
                    style={{transition: 'stroke-dasharray 0.8s ease-out'}}/>
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center">
              <div className="font-display text-3xl leading-none">{score}</div>
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">/ 100</div>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] uppercase tracking-widest font-bold text-terracotta">Compatibilità col tuo progetto</div>
          <div className="font-display text-lg mt-1">
            {score >= 85 ? 'Ottimo match!' : score >= 70 ? 'Buon match' : score >= 50 ? 'Match parziale' : 'Match basso'}
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            Basato su superficie, budget, zona, colture, caratteristiche e tipo operazione dichiarati.
          </div>
        </div>
      </div>

      {/* Breakdown */}
      <div className="mt-6 space-y-2">
        {(compat.breakdown || []).map((b, i) => (
          <div key={i} data-testid={`compat-factor-${b.factor.toLowerCase()}`}
               className={`flex items-center gap-3 rounded-lg border p-3 text-sm
                 ${b.status === 'ok' ? 'border-chartreuse/40 bg-chartreuse/5' :
                   b.status === 'warn' ? 'border-amber-500/30 bg-amber-500/5' :
                   b.status === 'ko' ? 'border-terracotta/40 bg-terracotta/5' :
                   'border-border'}`}>
            <span className="text-lg leading-none">{b.icon}</span>
            <div className="flex-1">
              <div className="font-medium text-[13px]">{b.factor}</div>
              <div className="text-xs text-muted-foreground">{b.label}</div>
            </div>
            <div className="text-xs font-mono tabular-nums text-muted-foreground shrink-0">{b.earned}/{b.weight}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
